"use client";
import { EditResourceRequest, IResource } from "@gettactic/api";
import {
  TabbedCrudDrawer,
  TabbedCrudDrawerTab
} from "@gettactic/components/src/overlay/CrudDrawer/TabbedCrudDrawer";
import { logError } from "@gettactic/helpers";
import { hasEnabledDeleteResource } from "@gettactic/helpers/src/featureFlags";
import { canManageResources } from "@gettactic/helpers/src/permissions";
import { useAreas, useAuthenticated } from "@gettactic/hooks";
import React, { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { BuilderResourceAssignments } from "@/features/map/components/builder-resource-editor/BuilderResourceAssignments";
import { BuilderResourceGeneralFields } from "@/features/map/components/builder-resource-editor/BuilderResourceGeneralFields";
import { BuilderResourceHours } from "@/features/map/components/builder-resource-editor/BuilderResourceHours";
import { capacityRequestFields } from "@/features/map/components/builder-resource-editor/capacityRequestFields";
import { CalendarsPanel } from "@/features/reserve/components/ResourceSettingsPanel/CalendarsPanel";
import { FloorSpacesPanel } from "@/features/reserve/components/ResourceSettingsPanel/FloorSpacesPanel";
import { IntegrationsExternalIdsPanel } from "@/features/reserve/components/ResourceSettingsPanel/IntegrationsExternalIdsPanel";
import { MapPanel } from "@/features/reserve/components/ResourceSettingsPanel/MapPanel";
import { useResourceSettingsState } from "@/features/reserve/hooks/useResourceSettings";
import { api } from "@/lib/api/api";
import { useS3Upload } from "@/lib/hooks/useS3Upload";
import { EntityType } from "@/lib/types/EntityType";
import { toEntity } from "@/lib/utils";
import { describeResourceSaveError } from "./resourceSaveError";

const haveAmenitiesChanged = (
  currentTags: { id: string }[] | undefined,
  amenityIds: string[]
): boolean => {
  const current = new Set((currentTags ?? []).map((tag) => tag.id));
  const next = new Set(amenityIds);
  return current.size !== next.size || [...next].some((id) => !current.has(id));
};

export interface ResourceSettingsPanelProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onEdit: (hasError: boolean) => void;
  onDelete?: () => Promise<void>;
}

export interface EditResourceForm
  extends Pick<
    IResource,
    "name" | "label" | "description" | "total_slots" | "is_approvable"
  > {
  maximum_slots_per_user: IResource["maximum_slots_per_user"] | null;
  parent_id: string | null;
  multiplicity: "single" | "multiple";
  is_multiple: boolean;
  is_open: boolean;
  is_walkup: boolean;
  /**
   * Hydrated by ResourceTicketTypesField from /v1/resources/{id} — the resource
   * these forms hold comes from the legacy office listing, which omits it.
   * `undefined` means "not loaded yet" and leaves the stored set untouched.
   */
  ticket_type_ids?: string[];
  is_unavailable: boolean;
  map_url: IResource["map_url"] | null;
  map_file: File | null;
  cover_image_file: File | null;
  amenities: string[];
}

export function getResourceType(entity: EntityType, isMultiple: boolean) {
  switch (entity) {
    case "parking":
      return isMultiple ? "parking_lot" : "parking_space";
    case "workspace":
      return isMultiple ? "workspace" : "desk";
    case "meeting_room":
      return entity;
    case "area":
    case "zone":
      return "area";
    default: {
      const exhaustiveCheck: string = entity;
      throw new Error(`Not handled entity type ${exhaustiveCheck}`);
    }
  }
}

export const ResourceSettingsPanel = ({
  isOpen,
  onOpenChange,
  onEdit,
  onDelete
}: ResourceSettingsPanelProps) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const calendarsSaveRef = useRef<(() => Promise<void>) | null>(null);
  const skipNextResetRef = useRef(false);
  const resourceContext = useResourceSettingsState();
  const resource = resourceContext.state.selected;
  // Admins and resource managers can move a resource and set priority
  // booking; zone managers get the rest of the panel without them.
  const canManageAllResources = canManageResources(authenticatedUser);

  const entity = resource
    ? toEntity(resource.type) || "workspace"
    : "workspace";
  const isMeetingRoom = entity === "meeting_room";
  const isFloor = resource?.type === "area" && resource?.parent_id === null;
  const isZone = resource?.type === "area" && resource?.parent_id !== null;
  const resourceOffice =
    authenticatedUser.offices?.byId[resource?.office_id ?? ""];
  const { uploadToS3 } = useS3Upload("/api/uploads/map", true);
  const { areas: areaList } = useAreas(resource?.office_id ?? null);
  const parentIdValue = useMemo<EditResourceForm["parent_id"]>(() => {
    return resource?.parent_id && areaList?.byId[resource.parent_id]
      ? resource.parent_id
      : null;
  }, [resource, areaList]);

  const defaultValues: EditResourceForm = useMemo(() => {
    const isMultipleValue = resource
      ? !isMeetingRoom && resource.total_slots > 1
      : false;
    return {
      name: resource?.name ?? "",
      label: resource?.label ?? "",
      description: resource?.description ?? "",
      multiplicity: isMultipleValue ? "multiple" : "single",
      is_multiple: isMultipleValue,
      parent_id: parentIdValue,
      total_slots: resource?.total_slots ?? 1,
      maximum_slots_per_user: resource?.maximum_slots_per_user ?? null,
      is_open: resourceContext.state.selected?.is_open || false,
      is_approvable: resource?.is_approvable ?? false,
      ticket_type_ids: undefined,
      is_walkup: !!resource?.is_walkup,
      is_unavailable: !(resource?.is_available ?? true),
      map_url: resource?.map_url ?? null,
      map_file: null,
      cover_image_file: null,
      amenities: resource?.tags ? resource.tags.map((tag) => tag.id) : []
    };
  }, [
    resource,
    isMeetingRoom,
    parentIdValue,
    resourceContext.state.selected?.is_open
  ]);

  const form = useForm<EditResourceForm>({
    defaultValues
  });

  // Reset form when resource changes to make form pristine with new defaults
  const prevResourceIdRef = useRef(resource?.id);
  React.useEffect(() => {
    const resourceChanged = prevResourceIdRef.current !== resource?.id;
    prevResourceIdRef.current = resource?.id;

    // Always reset when switching to a different resource
    if (resourceChanged) {
      skipNextResetRef.current = false;
    }

    if (skipNextResetRef.current) {
      skipNextResetRef.current = false;
      return;
    }

    if (resource) {
      form.reset(defaultValues);
    }
  }, [resource?.id, defaultValues]);

  const { setValue } = form;

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState("general");
  const [isCalendarSaving, setIsCalendarSaving] = useState(false);

  const saveGeneral = async () => {
    const data = form.getValues();
    if (!resource) {
      return;
    }

    setLoading(true);

    const handleSaveFailure = (error: unknown) => {
      logError("Error saving resource:", error);
      setLoading(false);
      toast.error(describeResourceSaveError(error));
      onEdit(true);
    };

    // Upload cover image if selected
    if (data.cover_image_file) {
      try {
        await api.client.organizations.resourceMediaUpload(
          resource.id,
          data.cover_image_file,
          true
        );
      } catch (error) {
        handleSaveFailure(error);
        return;
      }
      setValue("cover_image_file", null);
    }

    let mapUrlNew = data.map_url;
    if (data.map_file) {
      const uploadRes = await uploadToS3(data.map_file, {
        organizationId: authenticatedUser.organization?.id ?? "",
        resourceId: resource.id,
        officeId: resource.office_id
      });
      setValue("map_file", null);
      mapUrlNew = uploadRes.url;
    }

    // Dedicated resources keep their booking invariants (is_open false,
    // untouched booking flags). Capacity is the exception for desks and
    // workspaces: it is how a shared workspace gets its seats.
    const isDedicated = !!resource.is_dedicated;

    const request: EditResourceRequest = {
      ...capacityRequestFields(
        resource,
        getResourceType(entity, data.is_multiple),
        {
          isMultiple: data.is_multiple,
          totalSlots: data.total_slots,
          isMeetingRoom
        }
      ),
      // A non-admin cannot move the resource, and the hidden picker may not
      // have loaded its parent: always send the parent it already has.
      parent_id: canManageAllResources
        ? data.parent_id || null
        : (resource.parent_id ?? null),
      name: data.name,
      maximum_slots_per_user: data.maximum_slots_per_user ?? 1,
      is_open: isDedicated ? false : resource.is_open,
      // Pass through so a General save never clears a dedicated resource.
      is_dedicated: resource.is_dedicated,
      is_approvable: isDedicated ? resource.is_approvable : data.is_approvable,
      // A dedicated resource is never gated, so the picker is not rendered for
      // one — leave its stored set alone rather than clearing it blind, exactly
      // as is_approvable is passed through above.
      ticket_type_ids: isDedicated
        ? undefined
        : data.is_approvable
          ? data.ticket_type_ids
          : [],
      is_walkup: isDedicated ? false : data.is_walkup,
      is_available: isDedicated ? resource.is_available : !data.is_unavailable,
      is_shareable: resource.is_shareable,
      weight: resource.weight || 0,
      map_url: mapUrlNew,
      map_data: resource.map_data,
      label: data.label,
      description: data.description
    };

    // resourcePut throws on a 4xx; catching it keeps the form from hanging on
    // "Applying changes…" and lets a refusal say why.
    let ok = false;
    try {
      ({ ok } = await api.client.organizations.resourcePut(
        resource.id,
        request
      ));
    } catch (error) {
      handleSaveFailure(error);
      return;
    }

    // Only touch amenities after a successful save — clearing tags on a
    // failed PUT would silently wipe them. Tag writes need a permission zone
    // managers lack, so skip them unless the amenities actually changed.
    if (ok && haveAmenitiesChanged(resource.tags, data.amenities)) {
      try {
        await api.client.tags.removeAllTagsFromResource(resource.id);
        if (data.amenities.length > 0) {
          await api.client.tags.addTagToResource(
            resource.id,
            data.amenities.map((id) => ({ tag_id: id }))
          );
        }
      } catch (error) {
        handleSaveFailure(error);
        return;
      }
    }

    setLoading(false);
    if (ok) {
      toast.success(resource ? "Changes saved" : "Created successfully");

      // Make form pristine by resetting with saved values and skip next useEffect reset
      const savedValues = form.getValues();
      form.reset(savedValues);

      skipNextResetRef.current = true;
    } else {
      toast.error(describeResourceSaveError(null));
    }
    onEdit(!ok);
  };

  const onEditSubmit = async () => {
    if (!resource) {
      return;
    }

    if (selectedTab === "calendars") {
      await calendarsSaveRef.current?.();
      return;
    }

    await saveGeneral();
  };

  const canDelete =
    onDelete && hasEnabledDeleteResource(authenticatedUser, resource);

  const tabs: TabbedCrudDrawerTab<EditResourceForm>[] = [
    {
      id: "general",
      title: "General",
      isVisible: true,
      showSaveButton: true,
      render: ({ form }) =>
        resource ? (
          <div className="py-4 pr-2">
            <BuilderResourceGeneralFields
              form={form!}
              resource={resource}
              showMedia
              canMove={canManageAllResources}
            />
          </div>
        ) : null
    },
    {
      id: "hours",
      title: "Hours",
      isVisible: resource?.type !== "area",
      showSaveButton: false,
      render: () =>
        resourceOffice && resource ? (
          <div className="py-4">
            <BuilderResourceHours
              resources={[resource]}
              office={resourceOffice}
            />
          </div>
        ) : null
    },
    {
      id: "restrictions",
      title: "Access & Restrictions",
      isVisible: resource?.type !== "area",
      showSaveButton: false,
      render: () =>
        resource ? (
          <div className="py-4">
            <BuilderResourceAssignments
              mutateAll={onEdit}
              resources={[resource]}
              canEditAdditionalAccess={canManageAllResources}
            />
          </div>
        ) : null
    },
    {
      id: "integrations",
      title: "Integrations",
      isVisible: resource?.type !== "area",
      showSaveButton: false,
      render: () =>
        resourceOffice && resource ? (
          <IntegrationsExternalIdsPanel
            resources={[resource]}
            office={resourceOffice}
          />
        ) : null
    },
    {
      id: "tab-map",
      title: "Map",
      isVisible: resource?.type === "area",
      render: ({ form }) => (
        <MapPanel
          watch={form!.watch}
          setValue={form!.setValue}
          loading={loading}
        />
      )
    },
    {
      id: "spaces",
      title: "Spaces",
      isVisible: isFloor,
      render: () => (resource ? <FloorSpacesPanel floor={resource} /> : null)
    },
    {
      id: "calendars",
      title: "Calendars",
      isVisible: resource?.type === "meeting_room",
      showSaveButton: true,
      render: () =>
        resource ? (
          <CalendarsPanel
            resource={resource}
            saveRef={calendarsSaveRef}
            onSavingChange={setIsCalendarSaving}
          />
        ) : null
    }
  ];

  // Reset selected tab when resource changes
  React.useEffect(() => {
    setSelectedTab("general");
  }, [resource?.id]);

  const getTitle = () => {
    if (!resource) return "Edit";
    if (isFloor) return `Edit Floor: ${resource.name}`;
    if (isZone) return `Edit Zone: ${resource.name}`;
    return `Edit ${resource.name}`;
  };

  return (
    <TabbedCrudDrawer
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={getTitle()}
      form={form}
      tabs={tabs}
      selectedTab={selectedTab}
      onTabChange={setSelectedTab}
      onSubmit={onEditSubmit}
      onDelete={canDelete ? onDelete : undefined}
      isEditing
      submitLabel="Save"
      cancelLabel="Close"
      confirmWhenDirty
      submitProps={{ loading: loading || isCalendarSaving }}
      cancelProps={{ disabled: loading || isCalendarSaving }}
    />
  );
};
